/**
 * DraftingAttachments - the matters this document is linked to.
 *
 * This was a strip of tiny `bg-primary-50` pills with a bare `+` glyph, built
 * for the page HEADER it used to sit in - two pills wide, everything past the
 * second folded into a hover-only "+2 more" dropdown, and unlink was an ✕ that
 * only appeared on hover of a 20px chip. It lives in the Linked matters PANE
 * now (see `surface/DraftingAccessPane`), which is a column with room, so it
 * is a proper list: one row per matter, every matter visible, the matter glyph
 * leading, unlink revealed on the row, and one real control to link another.
 *
 * `useDraftingMatterLinks` is the same state without the markup - the page
 * uses it to put the "link a matter" control in the pane's header alongside
 * every other pane control on this surface.
 */

import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PiBriefcaseLight,
  PiLinkBreakLight,
  PiPlusLight,
} from 'react-icons/pi';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  removeDraftingAttachment,
  DraftingAttachment,
  DraftingItem,
} from '../../store/slices/draftingSlice';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import ListRow, { MetaDot, RowGlyph } from '../shared/surface/ListRow';
import { Well } from '../shared/brand/Field';

// Stable reference so the selector never returns a fresh array.
const EMPTY_MATTERS: any[] = [];

const MODAL_ICON = (
  <PiBriefcaseLight size={20} className="text-primary-600 dark:text-primary-300" />
);

export interface MatterLite {
  /** draft_attachments.id - what the DELETE route expects. */
  attachmentId: number;
  /** matters.id - used for "existing ids" pre-selection in the picker. */
  id: number;
  uuid: string;
  title: string;
  /*
    Everything below is BEST-EFFORT and any of it may be absent.

    A linked matter is often not in the matters cache - that list loads a page
    at a time and this document may be the only reason the matter is on screen
    - so the row is built from whatever three sources can supply between them:
    the cache, the backend's resolved fields on the draft itself, and the
    attachment row. The row renders the facts it has and says nothing about
    the ones it doesn't.
  */
  reference?: string | null;
  client?: string | null;
  jurisdiction?: string | null;
  status?: string | null;
  /** When this document was linked - from the attachment, so always known. */
  linkedAt?: string | null;
}

/**
 * Is this value fit to PRINT under a matter's name?
 *
 * A reference, a client or a status is short. Anything long and unbroken is a
 * token, an id or ciphertext that some endpoint happens to spell with the same
 * field name - and printing it makes the row look broken. Cheap guard, and the
 * cost of being wrong is one omitted fact rather than a wall of base64.
 */
const OPAQUE_TOKEN = /^[A-Za-z0-9+/=_-]{24,}$/;
const isDisplayableFact = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return text.length > 0 && text.length <= 40 && !OPAQUE_TOKEN.test(text);
};

interface DraftingAttachmentsProps {
  item: DraftingItem;
}

/** Resolve a matter attachment to a displayable {id, uuid, title} record.
 *
 * Prefers the redux matters cache, but that often doesn't hold this matter
 * (the list only loads a page at a time), which is why it used to fall back
 * to "Matter #<id>". For the PRIMARY matter (first attachment - the one the
 * backend resolves into item.matter_title/matter_uuid) we use those
 * backend-provided values so the real name shows and navigation works. */
function toMatterLite(
  attachment: DraftingAttachment,
  matters: any[],
  primary?: {
    title?: string | null;
    uuid?: string | null;
    jurisdiction?: string | null;
  },
): MatterLite {
  const found = matters.find((m: any) => Number(m?.id) === attachment.entity_id);
  const uuid = found?.uuid || primary?.uuid || '';
  const title =
    found?.title || found?.name || primary?.title || `Matter #${attachment.entity_id}`;
  const firstOf = (...values: any[]) => values.find(isDisplayableFact) ?? null;
  return {
    attachmentId: attachment.id,
    id: attachment.entity_id,
    uuid,
    title,
    // Matter payloads spell these differently across endpoints, so each fact
    // is taken from the first spelling that actually carries a value.
    reference: firstOf(found?.matter_number, found?.reference, found?.case_number),
    client: firstOf(found?.client_name, found?.client?.name, found?.client),
    /*
      CACHE ONLY. The draft's own `matter_jurisdiction` is not a jurisdiction
      on every record - on this backend it can hold an opaque encrypted blob
      ("RUExMMAsgRyCG7mBj6GtLAAE63IIRhFLFloJOXi7pUIA"), which is how a
      45-character token ended up printed under a matter's name. A real matter
      record is the only source trusted to describe itself.
    */
    jurisdiction: firstOf(found?.jurisdiction),
    status: firstOf(found?.status, found?.matter_status),
    linkedAt: attachment.created_at ?? null,
  };
}

export interface DraftingMatterLinks {
  matters: MatterLite[];
  /** Open the rich matter picker. */
  openPicker: () => void;
  /** Confirm, then detach - bound per matter so it can name it. */
  unlink: (matter: MatterLite) => void;
  /** Navigate to a matter, when the linkage resolved to a uuid. */
  goToMatter: (uuid: string) => void;
}

/** The matter linkage of one drafted document, without any markup. */
export function useDraftingMatterLinks(item: DraftingItem): DraftingMatterLinks {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const attachments: DraftingAttachment[] = useMemo(() => item.attachments ?? [], [item.attachments]);
  const matters = useAppSelector(
    (s: RootState) => (s.matters?.matters as any[] | undefined) ?? EMPTY_MATTERS,
  );

  const matterAttachments = useMemo(
    () => attachments.filter((a) => a.entity_type === 'matter'),
    [attachments],
  );

  // The first matter attachment is the one the backend resolves into
  // item.matter_title/matter_uuid (both order by created_at asc), so hand those
  // through as the primary fallback.
  const matterLites = useMemo(
    () => matterAttachments.map((a, idx) =>
      toMatterLite(a, matters, idx === 0
        ? {
          title: item.matter_title,
          uuid: item.matter_uuid,
          jurisdiction: item.matter_jurisdiction,
        }
        : undefined)),
    [matterAttachments, matters, item.matter_title, item.matter_uuid, item.matter_jurisdiction],
  );

  const openPicker = useCallback(() => {
    dispatch(
      showModal({
        type: 'draftingAttachMatter',
        showModal: true,
        title: t('Attach to matter'),
        message: t('Attach to matter'),
        subMsg: t(
          'Link this draft to a matter so the document and any generated work product appear in the matter context.',
        ),
        icon: MODAL_ICON,
        draftingKind: item.kind,
        draftingUuid: item.uuid,
        existingMatterIds: matterAttachments.map((a) => a.entity_id),
      }),
    );
  }, [dispatch, t, item.kind, item.uuid, matterAttachments]);

  const unlink = useCallback((matter: MatterLite) => {
    dispatch(
      showModal({
        type: 'confirmation',
        showModal: true,
        title: t('Unlink matter'),
        message: t('Unlink this document from “{{matter}}”?', { matter: matter.title }),
        subText: t('The document will no longer appear in the matter context.'),
        variant: 'warning',
        confirmLabel: t('Unlink'),
        icon: <PiLinkBreakLight className="w-6 h-6 text-warning-600" />,
        onConfirm: async () => {
          try {
            await dispatch(
              removeDraftingAttachment({
                kind: item.kind,
                uuid: item.uuid,
                attachmentId: matter.attachmentId,
              }),
            );
          } finally {
            dispatch(hideModal());
          }
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, t, item.kind, item.uuid]);

  const goToMatter = useCallback((uuid: string) => {
    if (uuid) history.push(`/matters/${uuid}`);
  }, [history]);

  return { matters: matterLites, openPicker, unlink, goToMatter };
}

/**
 * The meta line under a linked matter's name.
 *
 * Renders only the facts that exist, dot-separated, and falls back to when the
 * link was made - which the attachment always carries - so the line is never
 * empty and never merely restates that this is a matter.
 */
const MatterFacts: React.FC<{ matter: MatterLite }> = ({ matter }) => {
  const { t } = useTranslation();
  const linked = matter.linkedAt
    ? new Date(matter.linkedAt.endsWith('Z') ? matter.linkedAt : `${matter.linkedAt}Z`)
    : null;
  const facts: React.ReactNode[] = [
    matter.reference,
    matter.client,
    matter.jurisdiction,
    matter.status ? formatMatterStatus(matter.status) : null,
    linked && !isNaN(linked.getTime())
      ? t('Linked {{date}}', {
        date: linked.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
      })
      : null,
  ]
    .filter(Boolean)
    /* Three at most: the pane is ~310px wide, and a fourth fact only pushed
       the others into an ellipsis. */
    .slice(0, 3);

  if (facts.length === 0) return null;
  return (
    <>
      {facts.map((fact, i) => (
        <React.Fragment key={i}>
          {i > 0 && <MetaDot />}
          <span className="truncate">{fact}</span>
        </React.Fragment>
      ))}
    </>
  );
};

/** `in_progress` → `In Progress`. Matter status spellings vary by endpoint. */
const formatMatterStatus = (status: string): string =>
  status.replace(/_/g, ' ').replace(/(^|\s)\S/g, (c) => c.toUpperCase());

function DraftingAttachments({ item }: Readonly<DraftingAttachmentsProps>) {
  const { t } = useTranslation();
  const { matters, openPicker, unlink, goToMatter } = useDraftingMatterLinks(item);

  /*
    THE EMPTY STATE, at pane scale.

    It used to be the shared `EmptyState` - a 48px icon, an 18px headline, two
    lines of prose and a primary button, with 40px of padding around all of
    it. That composition is built for an empty PAGE; dropped into a ~310px
    pane it was a block taller than the panel's whole content area, to say
    that a document has no matter attached.

    One dashed row, identical to the "link another" control below, and a line
    under it. Same action, a fifth of the height.
  */
  if (matters.length === 0) {
    return (
      <div className="space-y-1.5" data-testid="drafting-matters-empty">
        <button
          type="button"
          onClick={openPicker}
          data-testid="drafting-link-a-matter"
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed
                     border-hairline px-3 py-2 text-[12px] font-medium text-fg-body
                     transition-colors duration-200 ease-slide
                     hover:border-champagne/50 hover:bg-surface-sunken/60"
        >
          <PiBriefcaseLight size={14} className="text-fg-muted" />
          {t('Link a matter')}
        </button>
        <p className="px-1 text-[11px] leading-relaxed text-fg-muted">
          {t('Linked work appears in that matter’s context.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <ul className="space-y-1">
        {matters.map((m, idx) => (
          <li key={m.id}>
            <Well className="!p-0 overflow-hidden">
              <ListRow
                index={idx}
                data-testid={`drafting-matter-${m.id}`}
                glyph={<RowGlyph state="accent"><PiBriefcaseLight className="w-3.5 h-3.5" /></RowGlyph>}
                title={m.title}
                /*
                  The facts, not the category.

                  This line said "Matter" - under a row whose glyph is a
                  briefcase, in a pane titled Linked matters, next to a matter's
                  name. Three ways of being told the same thing and no way of
                  telling two matters apart. It now carries whatever is actually
                  known: the reference, the client, the jurisdiction, and when
                  the document was linked - the last of which comes off the
                  attachment, so there is always at least one real fact here.
                */
                meta={<MatterFacts matter={m} />}
                rightWidth="w-0"
                title_={m.uuid ? t('Open matter') : undefined}
                onClick={m.uuid ? () => goToMatter(m.uuid) : undefined}
                actions={(
                  <Button
                    variant="neutral"
                    size="small"
                    ariaLabel={t('Unlink matter')}
                    tooltip={t('Unlink matter')}
                    icon={<PiLinkBreakLight size={13} />}
                    onClick={() => unlink(m)}
                  />
                )}
              />
            </Well>
          </li>
        ))}
      </ul>

      {/*
        A quiet way to add another, not a second primary action.

        It was a full-width outline Button - the same weight as the row above
        it, so a pane holding one matter read as two controls of equal
        importance stacked on top of each other. A dashed hairline row is the
        standard "add to this list" affordance and sits under the list instead
        of competing with it.
      */}
      <button
        type="button"
        onClick={openPicker}
        data-testid="drafting-link-another-matter"
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed
                   border-hairline px-3 py-1.5 text-[11px] font-medium text-fg-muted
                   transition-colors duration-200 ease-slide
                   hover:border-champagne/50 hover:bg-surface-sunken/60 hover:text-fg-body"
      >
        <PiPlusLight size={12} />
        {t('Link another matter')}
      </button>
    </div>
  );
}

export default DraftingAttachments;
